/**
 * useSpeechInput — swappable speech-to-text abstraction for Nova chat.
 *
 * CURRENT IMPLEMENTATION: Browser-native Web Speech API
 * (SpeechRecognition / webkitSpeechRecognition).
 *
 * SWAPPABLE SEAM: To replace with a server-side STT (e.g. Azure Speech),
 * keep the same exported interface { supported, listening, error, start, stop }
 * and swap the internals of this file. The Nova chat UI calls only these five
 * values — it never references SpeechRecognition directly.
 *
 * @param {{ lang?: string, onInterim?: (text: string) => void, onFinal?: (text: string) => void }} opts
 * @returns {{ supported: boolean, listening: boolean, error: string|null, start: () => void, stop: () => void }}
 */
import { useState, useRef, useCallback, useEffect } from 'react';

const SpeechRecognitionCtor =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

export default function useSpeechInput({ lang = 'en-US', onInterim, onFinal } = {}) {
  const supported = !!SpeechRecognitionCtor;
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const onInterimRef = useRef(onInterim);
  const onFinalRef = useRef(onFinal);

  // Keep callback refs fresh without re-creating recognition
  onInterimRef.current = onInterim;
  onFinalRef.current = onFinal;

  const stop = useCallback(() => {
    if (recRef.current) {
      try { recRef.current.stop(); } catch { /* already stopped */ }
    }
    setListening(false);
  }, []);

  const start = useCallback(() => {
    if (!SpeechRecognitionCtor) return;
    setError(null);

    // Create a fresh instance each time (avoids stale-state bugs across starts)
    const rec = new SpeechRecognitionCtor();
    rec.lang = lang;
    rec.interimResults = true;
    rec.continuous = true;
    rec.maxAlternatives = 1;
    recRef.current = rec;

    rec.onresult = (e) => {
      let interim = '';
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const transcript = e.results[i][0].transcript;
        if (e.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }
      if (final && onFinalRef.current) onFinalRef.current(final);
      else if (interim && onInterimRef.current) onInterimRef.current(interim);
    };

    rec.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'permission-denied') {
        setError('Microphone access is off');
      } else if (e.error !== 'aborted' && e.error !== 'no-speech') {
        setError(e.error || 'Speech error');
      }
      setListening(false);
    };

    rec.onend = () => {
      setListening(false);
    };

    try {
      rec.start();
      setListening(true);
    } catch {
      setError('Could not start speech recognition');
      setListening(false);
    }
  }, [lang]);

  // Clean up on unmount — stop recognition so mic is released
  useEffect(() => {
    return () => {
      if (recRef.current) {
        try { recRef.current.stop(); } catch { /* ok */ }
        recRef.current = null;
      }
    };
  }, []);

  return { supported, listening, error, start, stop };
}
